
import { supabase } from "@/integrations/supabase/client";
import { Assessment } from "@/types/assessment";
import { mapDbAssessmentToModel, mapDbAssessmentsToModels } from "@/services/supabaseUtils";

// Lightweight summary type for dashboard (no nested categories/questions/answers)
export interface AssessmentSummary {
  id: string;
  title: string;
  description: string;
  isActive: boolean;
  status: string;
  createdAt: string;
  updatedAt: string;
  slug: string;
  version: number;
  isMaturityModel: boolean;
  categoryCount?: number;
}

// Get all assessments (full model with categories/questions/answers) — uses bulk batching
export const getAssessments = async (): Promise<Assessment[]> => {
  try {
    const { data: dbAssessments, error } = await supabase
      .from("assessments")
      .select("*");

    if (error) {
      console.error("Error fetching assessments:", error);
      throw error;
    }

    return await mapDbAssessmentsToModels(dbAssessments || []);
  } catch (error) {
    console.error("Error in getAssessments:", error);
    throw error;
  }
};

// Get lightweight assessment summaries (no nested data) — for dashboard
export const getAssessmentsSummary = async (): Promise<AssessmentSummary[]> => {
  try {
    const { data, error } = await supabase
      .from("assessments")
      .select("id, title, description, is_active, status, created_at, updated_at, slug, version, is_maturity_model");

    if (error) {
      console.error("Error fetching assessment summaries:", error);
      throw error;
    }

    // Get category counts in one query
    const { data: categoryCounts, error: catError } = await supabase
      .from("categories")
      .select("assessment_id");

    const countMap = new Map<string, number>();
    if (!catError && categoryCounts) {
      for (const c of categoryCounts) {
        countMap.set(c.assessment_id, (countMap.get(c.assessment_id) || 0) + 1);
      }
    }

    return (data || []).map((a) => ({
      id: a.id,
      title: a.title,
      description: a.description,
      isActive: a.is_active,
      status: a.status,
      createdAt: a.created_at,
      updatedAt: a.updated_at,
      slug: a.slug,
      version: a.version,
      isMaturityModel: a.is_maturity_model,
      categoryCount: countMap.get(a.id) || 0,
    }));
  } catch (error) {
    console.error("Error in getAssessmentsSummary:", error);
    throw error;
  }
};

// Get assessment by ID
export const getAssessmentById = async (id: string): Promise<Assessment | null> => {
  try {
    const { data: dbAssessment, error } = await supabase
      .from("assessments")
      .select("*")
      .eq("id", id)
      .single();

    if (error || !dbAssessment) {
      console.error("Error fetching assessment:", error);
      return null;
    }

    return await mapDbAssessmentToModel(dbAssessment);
  } catch (error) {
    console.error("Error in getAssessmentById:", error);
    return null;
  }
};

// Get assessment by slug
export const getAssessmentBySlug = async (slug: string): Promise<Assessment | null> => {
  try {
    const { data: dbAssessment, error } = await supabase
      .from("assessments")
      .select("*")
      .eq("slug", slug)
      .single();

    if (error || !dbAssessment) {
      console.error("Error fetching assessment by slug:", error);
      return null;
    }

    return await mapDbAssessmentToModel(dbAssessment);
  } catch (error) {
    console.error("Error in getAssessmentBySlug:", error);
    return null;
  }
};
